import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Navbar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const cursorRef = useRef(null);
    const cursorFollowerRef = useRef(null);
    const [showServicesDropdown, setShowServicesDropdown] = useState(false);
    const [showAboutDropdown, setShowAboutDropdown] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
    const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

    const closeMobileMenu = () => {
        setMobileMenuOpen(false);
        setMobileAboutOpen(false);
        setMobileServicesOpen(false);
    };

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [mobileMenuOpen]);

    useEffect(() => {
        // Custom cursor logic
        const cursor = cursorRef.current;
        const follower = cursorFollowerRef.current;

        if (cursor && follower) {
            document.addEventListener('mousemove', (e) => {
                cursor.style.left = e.clientX + 'px';
                cursor.style.top = e.clientY + 'px';
                
                setTimeout(() => {
                    follower.style.left = e.clientX + 'px';
                    follower.style.top = e.clientY + 'px';
                }, 100);
            });

            // Add hover effect
            const hoverElements = document.querySelectorAll('a, button, input[type="submit"]');
            hoverElements.forEach(el => {
                el.addEventListener('mouseenter', () => {
                    cursor.classList.add('active');
                    follower.classList.add('active');
                });
                el.addEventListener('mouseleave', () => {
                    cursor.classList.remove('active');
                    follower.classList.remove('active');
                });
            });
        }
    }, []);

    const isActive = (path) => {
        if (path === '/' && location.pathname === '/') return true;
        if (path !== '/' && location.pathname.startsWith(path)) return true;
        return false;
    };

    const handleHashClick = (e, hash) => {
        e.preventDefault();
        if (location.pathname !== '/') {
            // If not on home page, navigate to home with hash
            window.location.href = `/${hash}`;
        } else {
            // If on home page, smooth scroll to section
            const element = document.querySelector(hash);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    };

    const handleServiceClick = (serviceId) => {
        setShowServicesDropdown(false);
        
        // Check if already on services page
        if (location.pathname === '/services') {
            // Already on services page, just scroll
            const section = document.getElementById(serviceId);
            if (section) {
                const yOffset = -120; // Navbar height + some padding
                const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
                window.scrollTo({
                    top: y,
                    behavior: 'smooth'
                });
            }
        } else {
            // Navigate to services page first
            navigate('/services');
            
            // Wait for navigation to complete, then scroll
            setTimeout(() => {
                const section = document.getElementById(serviceId);
                if (section) {
                    const yOffset = -120; // Navbar height + some padding
                    const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({
                        top: y,
                        behavior: 'smooth'
                    });
                }
            }, 500);
        }
    };

    const servicesMenu = [
        { id: 'consulting', name: 'Consulting & Planning' },
        { id: 'space-planning', name: 'Space Planning' },
        { id: 'interior-concepts', name: 'Interior Concepts' },
        { id: 'material-sourcing', name: 'Material Sourcing' },
        { id: 'project-management', name: 'Project Management' },
        { id: 'custom-furniture', name: 'Custom Furniture' },
        { id: 'renovation', name: 'Renovation Services' }
    ];

    return (
        <>
            {/* Custom Cursor */}
            <div className="cursor" ref={cursorRef}></div>
            <div className="cursor-follower" ref={cursorFollowerRef}></div>

            {/* Navigation */}
            <nav>
                <Link to="/" className="logo" onClick={closeMobileMenu}>
                    <img src={`${process.env.PUBLIC_URL}/header-logo.png`} alt="Hav'n Interiors & Global Sourcing" /><p className="nav-logo-text" style={{ marginLeft: '10px' }}>Hav'n Interiors</p>
                </Link>
                <button
                    type="button"
                    className="nav-toggle"
                    aria-label="Toggle menu"
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                    <span className={mobileMenuOpen ? 'open' : ''} />
                    <span className={mobileMenuOpen ? 'open' : ''} />
                    <span className={mobileMenuOpen ? 'open' : ''} />
                </button>
                <ul className="nav-desktop">
                    <li>
                        <Link 
                            to="/" 
                            style={{ color: isActive('/') ? 'var(--accent-color)' : 'var(--text-light)' }}
                        >
                            Home
                        </Link>
                    </li>
                    <li 
                        className="has-dropdown"
                        onMouseEnter={() => setShowAboutDropdown(true)}
                        onMouseLeave={() => setShowAboutDropdown(false)}
                        style={{ position: 'relative' }}
                    >
                        {/* <Link 
                            to="/about" 
                            style={{ color: isActive('/about') ? 'var(--accent-color)' : 'var(--text-light)' }}
                        >
                            About
                        </Link> */}
                        <Link 
                            to="/about"
                            onClick={(e) => e.preventDefault()}
                            style={{ 
                                color: (isActive('/about') || isActive('/our-team')) ? 'var(--accent-color)' : 'var(--text-light)',
                                cursor: 'pointer',
                                pointerEvents: 'none'
                            }}
                        >
                            About
                        </Link>
                        
                        {showAboutDropdown && (
                            <div 
                                className="dropdown-menu" 
                                onMouseEnter={() => setShowAboutDropdown(true)}
                                onMouseLeave={() => setShowAboutDropdown(false)}
                                style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '0',
                                    paddingTop: '15px',
                                    zIndex: 10000
                                }}
                            >
                                <div style={{
                                    background: 'rgba(26, 26, 26, 0.98)',
                                    backdropFilter: 'blur(20px)',
                                    minWidth: '200px',
                                    borderRadius: '8px',
                                    padding: '15px 0',
                                    boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                                    border: '1px solid rgba(201, 169, 110, 0.3)'
                                }}>
                                    <Link
                                        to="/about"
                                        onClick={() => setShowAboutDropdown(false)}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            padding: '12px 25px',
                                            background: 'none',
                                            border: 'none',
                                            color: 'var(--text-light)',
                                            fontSize: '14px',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            borderLeft: '3px solid transparent',
                                            textDecoration: 'none'
                                        }}
                                        className="dropdown-item"
                                    >
                                        About Us
                                    </Link>
                                    <Link
                                        to="/our-team"
                                        onClick={() => setShowAboutDropdown(false)}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            padding: '12px 25px',
                                            background: 'none',
                                            border: 'none',
                                            color: 'var(--text-light)',
                                            fontSize: '14px',
                                            textAlign: 'left',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            borderLeft: '3px solid transparent',
                                            textDecoration: 'none'
                                        }}
                                        className="dropdown-item"
                                    >
                                        Our Team
                                    </Link>
                                </div>
                            </div>
                        )}
                    </li>
                    <li>
                        <Link 
                            to="/segments" 
                            style={{ color: isActive('/segments') ? 'var(--accent-color)' : 'var(--text-light)' }}
                        >
                            Segments
                        </Link>
                    </li>
                    <li 
                        className="has-dropdown"
                        onMouseEnter={() => setShowServicesDropdown(true)}
                        onMouseLeave={() => setShowServicesDropdown(false)}
                        style={{ position: 'relative' }}
                    >
                        <Link 
                            to="/services" 
                            style={{ 
                                color: isActive('/services') ? 'var(--accent-color)' : 'var(--text-light)'
                            }}
                        >
                            Services
                        </Link>
                        
                        {showServicesDropdown && (
                            <div 
                                className="dropdown-menu" 
                                onMouseEnter={() => setShowServicesDropdown(true)}
                                onMouseLeave={() => setShowServicesDropdown(false)}
                                style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: '0',
                                    paddingTop: '15px',
                                    zIndex: 10000
                                }}
                            >
                                <div style={{
                                    background: 'rgba(26, 26, 26, 0.98)',
                                    backdropFilter: 'blur(20px)',
                                    minWidth: '250px',
                                    borderRadius: '8px',
                                    padding: '15px 0',
                                    boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                                    border: '1px solid rgba(201, 169, 110, 0.3)'
                                }}
                            >
                                    {servicesMenu.map((service) => (
                                        <button
                                            key={service.id}
                                            onClick={() => handleServiceClick(service.id)}
                                            style={{
                                                width: '100%',
                                                padding: '12px 25px',
                                                background: 'none',
                                                border: 'none',
                                                color: 'var(--text-light)',
                                                fontSize: '14px',
                                                textAlign: 'left',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                borderLeft: '3px solid transparent'
                                            }}
                                            className="dropdown-item"
                                        >
                                            {service.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </li>
                    <li>
                        <Link 
                            to="/studios" 
                            style={{ color: isActive('/studios') ? 'var(--accent-color)' : 'var(--text-light)' }}
                        >
                            Studios
                        </Link>
                    </li>
                    <li>
                        <Link 
                            to="/cafe-connections" 
                            style={{ color: isActive('/cafe-connections') ? 'var(--accent-color)' : 'var(--text-light)' }}
                        >
                            Cafe Connections
                        </Link>
                    </li>
                    <li>
                        <Link 
                            to="/partnership" 
                            style={{ color: isActive('/partnership') ? 'var(--accent-color)' : 'var(--text-light)' }}
                        >
                            Partnership
                        </Link>
                    </li>
                    <li>
                        <Link 
                            to="/blog" 
                            style={{ color: isActive('/blog') ? 'var(--accent-color)' : 'var(--text-light)' }}
                        >
                            Blog
                        </Link>
                    </li>
                    <li>
                        <Link 
                            to="/contact" 
                            style={{ color: isActive('/contact') ? 'var(--accent-color)' : 'var(--text-light)' }}
                        >
                            Contact
                        </Link>
                    </li>
                </ul>
            </nav>

            {/* Mobile menu overlay */}
            <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`} aria-hidden={!mobileMenuOpen}>
                <div className="mobile-menu-backdrop" onClick={closeMobileMenu} />
                <div className="mobile-menu-panel">
                    <button
                        type="button"
                        className="mobile-menu-close-btn"
                        onClick={closeMobileMenu}
                        aria-label="Close menu"
                    >
                        ✕ Close
                    </button>
                    <ul className="mobile-menu-list">
                        <li><Link to="/" onClick={closeMobileMenu} style={{ color: isActive('/') ? 'var(--accent-color)' : 'var(--text-light)' }}>Home</Link></li>
                        <li className="mobile-has-sub">
                            <button type="button" className="mobile-sub-trigger" onClick={() => setMobileAboutOpen(!mobileAboutOpen)} style={{ color: (isActive('/about') || isActive('/our-team')) ? 'var(--accent-color)' : 'var(--text-light)', background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', padding: 0, font: 'inherit', letterSpacing: '1px', textTransform: 'uppercase' }}>
                                <span>About</span>
                                <span className={`mobile-sub-chevron ${mobileAboutOpen ? 'open' : ''}`} aria-hidden>▼</span>
                            </button>
                            {mobileAboutOpen && (
                                <ul className="mobile-sub-list">
                                    <li><Link to="/about" onClick={closeMobileMenu}>About Us</Link></li>
                                    <li><Link to="/our-team" onClick={closeMobileMenu}>Our Team</Link></li>
                                </ul>
                            )}
                        </li>
                        <li><Link to="/segments" onClick={closeMobileMenu} style={{ color: isActive('/segments') ? 'var(--accent-color)' : 'var(--text-light)' }}>Segments</Link></li>
                        <li className="mobile-has-sub">
                            <button type="button" className="mobile-sub-trigger" onClick={() => setMobileServicesOpen(!mobileServicesOpen)} style={{ color: isActive('/services') ? 'var(--accent-color)' : 'var(--text-light)', background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left', padding: 0, font: 'inherit', letterSpacing: '1px', textTransform: 'uppercase' }}>
                                <span>Services</span>
                                <span className={`mobile-sub-chevron ${mobileServicesOpen ? 'open' : ''}`} aria-hidden>▼</span>
                            </button>
                            {mobileServicesOpen && (
                                <ul className="mobile-sub-list">
                                    {servicesMenu.map((service) => (
                                        <li key={service.id}>
                                            <button type="button" onClick={() => { handleServiceClick(service.id); closeMobileMenu(); }} style={{ background: 'none', border: 'none', color: 'var(--text-light)', cursor: 'pointer', padding: 0, font: 'inherit', textAlign: 'left' }}>
                                                {service.name}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </li>
                        <li><Link to="/studios" onClick={closeMobileMenu} style={{ color: isActive('/studios') ? 'var(--accent-color)' : 'var(--text-light)' }}>Studios</Link></li>
                        <li><Link to="/cafe-connections" onClick={closeMobileMenu} style={{ color: isActive('/cafe-connections') ? 'var(--accent-color)' : 'var(--text-light)' }}>Cafe Connections</Link></li>
                        <li><Link to="/partnership" onClick={closeMobileMenu} style={{ color: isActive('/partnership') ? 'var(--accent-color)' : 'var(--text-light)' }}>Partnership</Link></li>
                        <li><Link to="/blog" onClick={closeMobileMenu} style={{ color: isActive('/blog') ? 'var(--accent-color)' : 'var(--text-light)' }}>Blog</Link></li>
                        <li><Link to="/contact" onClick={closeMobileMenu} style={{ color: isActive('/contact') ? 'var(--accent-color)' : 'var(--text-light)' }}>Contact</Link></li>
                    </ul>
                </div>
            </div>

            {/* Dropdown CSS */}
            <style jsx>{`
                .dropdown-item:hover {
                    background: rgba(201, 169, 110, 0.15) !important;
                    color: var(--accent-color) !important;
                    border-left-color: var(--accent-color) !important;
                }
            `}</style>
        </>
    );
};

export default Navbar;

